// @ts-strict-ignore
import produce from 'immer';
import React, {useEffect, useMemo, useState} from 'react';

import {InputReact} from 'common/components/input';
import {InputSize, InputType, ValidationStatus} from 'common/components/input/types';
import {MetricEntry} from 'common/query-builder/query-entry-section/types';
import {getFormulaValidationError} from 'common/report/insights/contexts/formula-utils';
import {getAlphaLabelFromIndex} from 'common/util';
import {KEY_VALUES} from 'common/util/dom';
import {DraggableEntry} from 'common/components/draggable-list';
import {TopLevelMetricType} from 'common/types/reports/bookmark';
import {BookmarkDragMetadata} from 'common/report/insights/contexts/drag-drop/types';
import {MetricSelector} from 'iron/common/query-builder/query-entry-section/row-types/new-metric-entry/common/metric-selector';
import {getNewSimpleMetricEntry} from 'iron/common/query-builder/query-entry-section/row-types/new-metric-entry/utils';
import {useTranslation} from 'common/i18n/hooks';
import {EventRowSkeleton} from 'iron/common/query-builder/query-entry-section/row-types/new-metric-entry/common/event-row';

// eslint-disable-next-line import/no-cycle
import {NewMetricEntry} from '../..';
import type {RowProps} from '../catalog';
import styles from './index.module.styl';

export function FormulaDefinition(props: RowProps) {
  const {t} = useTranslation();
  const [formulaDefinition, setFormulaDefinition] = useState(props.entry.definition);

  useEffect(() => {
    setFormulaDefinition(props.entry.definition);
  }, [props.entry.definition]);

  const formulaError = useMemo(
    () =>
      getFormulaValidationError({
        metric: props.entry,
        definition: formulaDefinition,
        globalMetrics: props.getEntries(false) as MetricEntry[],
      }),
    [props.getEntries().length, formulaDefinition, props.entry.referencedMetrics?.length ?? 0],
  );

  const onDefinitionCommit = (newDefinition: string) => {
    if (newDefinition && props.entry.definition !== newDefinition) {
      props.updateEntry({
        ...props.entry,
        definition: newDefinition,
      });
    }
  };

  /** commits the formula on Enter */
  const handleDefinitionKeydown = (ev: KeyboardEvent) => {
    const inputEl = ev.currentTarget as HTMLInputElement;
    if (ev.key === KEY_VALUES.ENTER) {
      ev.preventDefault();
      inputEl.blur();
    } else if (ev.key === KEY_VALUES.ESCAPE) {
      setFormulaDefinition(props.entry.definition);
      inputEl.blur();
    }
  };

  /** transforms the input to uppercase */
  const handleDefinitionInput = (ev: InputEvent) => {
    const inputEl = ev.currentTarget as HTMLInputElement;
    inputEl.value = inputEl.value.toUpperCase();
    setFormulaDefinition(inputEl.value);
  };

  const handleDefinitionBlur = (ev: FocusEvent) => {
    const inputEl = ev.currentTarget as HTMLInputElement;
    const newDefinition = inputEl.value;

    if (!newDefinition && props.entry.definition) {
      setFormulaDefinition(props.entry.definition);
    } else {
      onDefinitionCommit((ev.currentTarget as HTMLInputElement).value);
    }
  };

  const updateReferenceEntry = (index: number, entry: MetricEntry) => {
    props.updateEntry(
      produce(props.entry, (draft) => {
        draft.referencedMetrics[index] = entry;
      }),
    );
  };

  const removeReferenceEntry = (index: number) => {
    props.updateEntry(
      produce(props.entry, (draft) => {
        draft.referencedMetrics.splice(index, 1);
      }),
    );
  };

  const addReferenceEntry = (index: number, entry: MetricEntry) => {
    props.updateEntry(
      produce(props.entry, (draft) => {
        draft.referencedMetrics.splice(index + 1, 0, entry);
      }),
    );
  };

  return (
    <div className={styles.formula}>
      {/* formula definition, i.e. A / B * 100 */}
      <div className={styles.definition}>
        <InputReact
          className={styles.input}
          elref='formulaDefinition'
          type={InputType.Text}
          size={InputSize.Medium}
          placeholder='eg. (A+B)/(A-B) * 100'
          autoFocus={props.entry.definition === ``}
          {...(formulaError && {
            validationStatus: ValidationStatus.Invalid,
            errorMessage: formulaError,
          })}
          value={formulaDefinition}
          onKeydown={handleDefinitionKeydown}
          onInput={handleDefinitionInput}
          onBlur={handleDefinitionBlur}
        />
      </div>

      <div className={styles.referencedMetrics}>
        {/* for saved formulas, renders metrics involved in the formula definition */}
        {Array.isArray(props.entry.referencedMetrics) &&
          props.entry.referencedMetrics.map((entry, metricIndex) => (
            <DraggableEntry<BookmarkDragMetadata>
              data={{
                index: metricIndex,
                type: TopLevelMetricType.Metric,
                group: `${props.entry._idx!}-metrics`,
                parentMetricIndex: props.index,
              }}
              isLast={metricIndex === props.entry.referencedMetrics.length - 1}
            >
              <NewMetricEntry
                {...props}
                key={entry._idx}
                disableHideEntry
                isExpanded={!entry.name}
                parentMetricIndex={props.index}
                entry={entry}
                index={metricIndex}
                updateEntry={(metricEntry) => updateReferenceEntry(metricIndex, metricEntry)}
                cloneEntry={(metricEntry) => addReferenceEntry(metricIndex, metricEntry)}
                removeEntry={() => removeReferenceEntry(metricIndex)}
                rowLabel={getAlphaLabelFromIndex(metricIndex)}
              />
            </DraggableEntry>
          ))}
      </div>

      {/* add metric to formula */}
      <EventRowSkeleton
        {...props}
        isSecondaryRow
        rowIcon='behavior-event-add-step'
        activeMenuType='metric-add-selector'
        contentEl={
          <MetricSelector
            {...props}
            disableContextPanel
            index={-1}
            parentMetricIndex={props.index}
            activeMenuType='metric-add-selector'
            entry={{
              name: t(`query-builder.add-metric`),
              ...getNewSimpleMetricEntry({isExpanded: false}),
            }}
          />
        }
      />
    </div>
  );
}
